/**
 * Phase 2 — widget WebSocket hang bridge (chatbot-voice agent/ws_bridge.py).
 * Ha nincs voice_ws_url → a chat.js V1 HTTP útvonalat használja.
 */

/**
 * @param {{
 *   wsUrl: string,
 *   getConversationId: () => string|null,
 *   getVoiceToken: () => Promise<string|null>,
 *   onTranscript: (text: string) => void,
 *   onReply: (text: string) => void,
 *   onVoiceDisabled: () => void,
 *   onError: (message: string) => void,
 *   onState: (state: 'listening'|'processing'|'speaking'|'idle') => void,
 * }} handlers
 */
export function createVoiceWsSession(handlers) {
    /** @type {WebSocket|null} */
    let socket = null;
    /** @type {AudioContext|null} */
    let audioCtx = null;
    /** @type {ScriptProcessorNode|null} */
    let processor = null;
    /** @type {MediaStreamAudioSourceNode|null} */
    let source = null;
    /** @type {MediaStream|null} */
    let mediaStream = null;
    /** @type {HTMLAudioElement|null} */
    let playback = null;
    /** @type {ReturnType<typeof setInterval>|null} */
    let silenceTimer = null;

    let active = false;
    let speaking = false;
    let awaitingReply = false;
    let speechSeen = false;
    let silentMs = 0;

    function sendJson(obj) {
        if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(obj));
        }
    }

    function stopPlayback() {
        if (playback) {
            try {
                playback.pause();
                playback.src = '';
            } catch {
                // ignore
            }
            playback = null;
        }
    }

    function resumeListening() {
        speaking = false;
        awaitingReply = false;
        speechSeen = false;
        silentMs = 0;
        if (active) {
            handlers.onState('listening');
        }
    }

    function stopMic() {
        if (silenceTimer) {
            clearInterval(silenceTimer);
            silenceTimer = null;
        }
        try {
            processor?.disconnect();
        } catch {
            // ignore
        }
        try {
            source?.disconnect();
        } catch {
            // ignore
        }
        processor = null;
        source = null;
        if (mediaStream) {
            mediaStream.getTracks().forEach((t) => t.stop());
            mediaStream = null;
        }
    }

    function armSilenceWatch() {
        speechSeen = false;
        silentMs = 0;
        if (silenceTimer) {
            clearInterval(silenceTimer);
        }
        silenceTimer = setInterval(() => {
            if (! active || speaking || awaitingReply) {
                silentMs = 0;

                return;
            }
            if (! speechSeen) {
                return;
            }
            silentMs += 200;
            // 800 ms: gyorsabb kör; túl rövid → félbevágott mondatok
            if (silentMs >= 800) {
                silentMs = 0;
                speechSeen = false;
                awaitingReply = true;
                sendJson({ type: 'utterance_end' });
                handlers.onState('processing');
            }
        }, 200);
    }

    async function ensureSocket() {
        if (socket && socket.readyState === WebSocket.OPEN) {
            return socket;
        }

        const token = await handlers.getVoiceToken();
        const conversationId = handlers.getConversationId();
        if (! token || ! conversationId) {
            throw new Error('Hiányzó voice token vagy conversation.');
        }

        await new Promise((resolve, reject) => {
            const ws = new WebSocket(handlers.wsUrl);
            socket = ws;
            ws.binaryType = 'arraybuffer';

            const timer = setTimeout(() => {
                reject(new Error('Voice WS timeout'));
                try {
                    ws.close();
                } catch {
                    // ignore
                }
            }, 10000);

            ws.onopen = () => {
                sendJson({
                    type: 'hello',
                    conversation_id: conversationId,
                    voice_token: token,
                });
            };

            ws.onmessage = async (ev) => {
                if (typeof ev.data !== 'string') {
                    return;
                }
                let msg;
                try {
                    msg = JSON.parse(ev.data);
                } catch {
                    return;
                }

                if (msg.type === 'ready') {
                    clearTimeout(timer);
                    resolve(ws);

                    return;
                }

                if (msg.type === 'transcript' && msg.text) {
                    handlers.onTranscript(msg.text);
                    handlers.onState('processing');
                }

                if (msg.type === 'reply' && msg.text) {
                    handlers.onReply(msg.text);
                }

                // Üres válasz / nincs TTS: szerver tts_done nélkül is vissza kell állni hallgatásra
                if (msg.type === 'reply' && ! msg.text && active && ! playback) {
                    resumeListening();
                }

                if (msg.type === 'tts' && msg.audio_base64) {
                    stopPlayback();
                    speaking = true;
                    handlers.onState('speaking');
                    const bin = atob(msg.audio_base64);
                    const bytes = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i += 1) {
                        bytes[i] = bin.charCodeAt(i);
                    }
                    const blob = new Blob([bytes], { type: msg.mime || 'audio/mpeg' });
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    playback = audio;
                    try {
                        await audio.play();
                        await new Promise((res) => {
                            audio.onended = () => res();
                            audio.onerror = () => res();
                        });
                    } finally {
                        URL.revokeObjectURL(url);
                        if (playback === audio) {
                            playback = null;
                        }
                        // A hallgatás visszaállítása a kliens lejátszásához kötött —
                        // a szerver tts_done gyakran elvész (WS zárás / rossz időbecslés).
                        resumeListening();
                    }
                }

                if (msg.type === 'tts_done') {
                    // Csak ha már nincs folyamatban lejátszás (különben a finally intézi)
                    if (! playback) {
                        resumeListening();
                    }
                }

                if (msg.type === 'voice_disabled') {
                    handlers.onVoiceDisabled();
                }

                if (msg.type === 'error') {
                    handlers.onError(msg.message || 'voice_ws_error');
                    if (! playback) {
                        resumeListening();
                    }
                }
            };

            ws.onerror = () => {
                clearTimeout(timer);
                reject(new Error('Voice WS hiba'));
            };

            ws.onclose = () => {
                socket = null;
            };
        });

        return socket;
    }

    async function startMic() {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        audioCtx = audioCtx || new AudioCtx();
        if (audioCtx.state === 'suspended') {
            await audioCtx.resume();
        }

        mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
            },
        });

        source = audioCtx.createMediaStreamSource(mediaStream);
        const bufferSize = 4096;
        processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
        processor.onaudioprocess = (e) => {
            if (! active || ! socket || socket.readyState !== WebSocket.OPEN) {
                return;
            }
            const input = e.inputBuffer.getChannelData(0);
            let sum = 0;
            for (let i = 0; i < input.length; i += 1) {
                sum += input[i] * input[i];
            }
            const rms = Math.sqrt(sum / input.length);

            // Barge-in: bot beszéd közben beleszólás → leállítjuk a TTS-t, újra hallgatunk
            if (speaking && rms > 0.05) {
                stopPlayback();
                sendJson({ type: 'interrupt' });
                resumeListening();

                return;
            }

            // Ne küldjünk PCM-et processing / TTS alatt — szennyezi a buffert és hamis interruptot okoz
            if (speaking || awaitingReply) {
                return;
            }

            if (rms > 0.02) {
                speechSeen = true;
                silentMs = 0;
            }

            const inRate = audioCtx.sampleRate;
            const outRate = 16000;
            const ratio = inRate / outRate;
            const outLen = Math.floor(input.length / ratio);
            const pcm = new Int16Array(outLen);
            for (let i = 0; i < outLen; i += 1) {
                const idx = Math.floor(i * ratio);
                const s = Math.max(-1, Math.min(1, input[idx]));
                pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
            }
            socket.send(pcm.buffer);
        };

        source.connect(processor);
        // Néma kimenet: a ScriptProcessor-nek kell destination, de a mic echo tilos
        const mute = audioCtx.createGain();
        mute.gain.value = 0;
        processor.connect(mute);
        mute.connect(audioCtx.destination);
        armSilenceWatch();
        handlers.onState('listening');
    }

    return {
        async start() {
            active = true;
            awaitingReply = false;
            await ensureSocket();
            await startMic();
        },
        stop() {
            active = false;
            awaitingReply = false;
            stopPlayback();
            stopMic();
            try {
                socket?.close();
            } catch {
                // ignore
            }
            socket = null;
            handlers.onState('idle');
        },
        get active() {
            return active;
        },
    };
}
